import { motion } from "framer-motion";
import { Building2, Smartphone, Briefcase, CreditCard, Cpu } from "lucide-react";
import { Reveal } from "@/lib/motion";

const TIERS = [
  {
    tag: "Parent Company",
    title: "ArcaCentum",
    desc: "The AI technology company and home for the entire product portfolio.",
    icon: Building2,
    level: 0,
    accent: "from-violet-400 to-cyan-300",
  },
  {
    tag: "Flagship AI App Studio · In Development",
    title: "ArcaCentum.ai",
    desc: "An AI-native app studio turning ideas into real iOS & Android apps.",
    icon: Smartphone,
    level: 1,
    accent: "from-violet-400/80 to-violet-300/60",
  },
  {
    tag: "Business Building & Consulting Platform · In Development",
    title: "ArcaCapitalis",
    desc: "Strategic business building and consulting to help founders build strong foundations.",
    icon: Briefcase,
    level: 1,
    accent: "from-cyan-400/80 to-cyan-300/60",
  },
  {
    tag: "AI Business & Personal Credit Platform · In Development",
    title: "ArcaCentum Credit",
    desc: "An AI-powered platform to understand, organize, build, and manage business and personal credit.",
    icon: CreditCard,
    level: 1,
    accent: "from-blue-400/80 to-purple-400/60",
  },
  {
    tag: "In Development",
    title: "Future ArcaCentum Products",
    desc: "Additional AI-powered products in development.",
    icon: Cpu,
    level: 2,
    accent: "from-white/25 to-white/10",
  },
];

export const Ecosystem = () => (
  <section id="ecosystem" data-testid="ecosystem-section" className="relative py-28 sm:py-40">
    <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] glow-violet opacity-30" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl mb-16">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-cyan-300/80">
            05 — Company Ecosystem
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-500 tracking-[-0.02em] text-white">
            One company. A structured portfolio.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-base text-white/50 font-light">
            ArcaCentum is the parent. Every product below sits within its
            portfolio, each with a clear role.
          </p>
        </Reveal>
      </div>

      <div className="relative flex flex-col items-center gap-4">
        {/* connecting spine */}
        <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-violet-400/40 via-white/10 to-transparent hidden sm:block" />

        {TIERS.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.08} className="w-full">
            <div
              data-testid={`ecosystem-tier-${i}`}
              className={`relative mx-auto ${
                t.level === 0 ? "max-w-3xl" : t.level === 1 ? "max-w-2xl" : "max-w-xl"
              }`}
              style={{ marginTop: i === 0 ? 0 : undefined }}
            >
              <div
                className={`glass glass-hover flex items-center gap-5 rounded-2xl p-5 sm:p-6 ${
                  t.level === 0 ? "border-violet-400/25" : ""
                }`}
              >
                <div
                  className={`shrink-0 inline-flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl bg-gradient-to-br ${t.accent} text-[#0a0a0f]`}
                >
                  <t.icon size={t.level === 0 ? 26 : 22} strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {t.tag}
                  </span>
                  <h3
                    className={`font-display font-600 text-white ${
                      t.level === 0 ? "text-xl sm:text-2xl" : "text-lg sm:text-xl"
                    }`}
                  >
                    {t.title}
                  </h3>
                  <p className="mt-1 text-sm text-white/45 font-light">{t.desc}</p>
                </div>
              </div>
              {i < TIERS.length - 1 && (
                <div className="flex justify-center my-1">
                  <motion.div
                    className="h-6 w-px bg-gradient-to-b from-white/20 to-transparent"
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  />
                </div>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
